import { useLangFile } from '../../context/LanguageContext';
import React, { memo, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Card, ListItem, Checkbox } from '@mui/material';
import PropTypes from 'prop-types';
import { voidFunc } from '../../constants/PropTypeUtils';
import { getFieldName } from '../../helpers/StateInterpreters';
import Styles from './PrescriptionArchiveExpandableListItem.module.less';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import { PrescriptionJob } from '../Prescription/PrescriptionJob';
import TableHead from '@mui/material/TableHead';
import PrescriptionTableRow from './PrescriptionTableRow';
import IconButton from '@mui/material/IconButton';
import { TableColumns } from './PrescriptionArchiveUtils';
import moment from 'moment';
import { useSeasonContext } from '../../context/SeasonContext';
import { PRESCRIPTION_UNIT } from '../../constants/PrescriptionConstants';

const PrescriptionArchiveExpandableListItem = (
  props: PrescriptionArchiveExpandableListItem.propTypes
) => {
  const LangFile = useLangFile();
  const { selectedSeason } = useSeasonContext();

  const cols = [
    { id: TableColumns.jobName, label: LangFile.PrescriptionTableHead.name },
    { id: TableColumns.jobType, label: LangFile.PrescriptionTableHead.jobType },
    { id: TableColumns.createdDate, label: LangFile.PrescriptionTableHead.created },
    { id: TableColumns.layer, label: LangFile.PrescriptionTableHead.inputLayer },
    { id: TableColumns.totalPrescription, label: LangFile.PrescriptionTableHead.totalPrescription },
  ];

  const [expanded, setExpanded] = useState(false);

  const handleTopClicked = useCallback((event) => {
    setExpanded((currentValue) => !currentValue);
  }, []);

  const firstJob = props.jobs.length > 0 ? props.jobs[0] : null;
  const hasRelatedJob = firstJob !== null && firstJob.vrmSet != null;
  const firstJobRelatedJobName = hasRelatedJob
    ? props.jobs.find((j) => j.jobId === firstJob.vrmSet.sourceVrmId)?.jobName
    : undefined;

  const isJobSelected = (job) =>
    props.selectedJobs?.some((selectedJob) => selectedJob.jobId === job.jobId);

  const fieldJobsChecked = [...props.jobs, ...props.outsideJobs].every(isJobSelected);

  const formatTotalPrescription = (job) => {
    let unitString;
    const unit = job.unit ? job.unit : job.allocationUnit;
    switch (unit) {
      case PRESCRIPTION_UNIT.LITER:
        unitString = LangFile.PrescriptionSettings.unit.liters.short;
        break;
      case PRESCRIPTION_UNIT.PIECES_M2:
        unitString = LangFile.PrescriptionSettings.unit.pieces.short;
        break;
      default:
        unitString = LangFile.PrescriptionSettings.unit.kilogram.short;
        break;
    }
    return `${job.totalPrescription.toFixed(2)} ${unitString}`;
  };

  return (
    <ListItem className={Styles.listItem}>
      <Card className={Styles.listCard}>
        <Box
          data-testid={`prescription-archive-field-${props.field.fieldId}`}
          className={Styles.top}
          onClick={handleTopClicked}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Box
            className={Styles.titleTop}
            sx={{
              flexGrow: expanded ? 1 : 0,
            }}
          >
            <Box className={Styles.header}>
              <Typography variant={'subtitle2'} classes={{ root: Styles.fieldName }}>
                <span onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    disableRipple
                    color="default"
                    onChange={(event) => {
                      event.stopPropagation();
                      props.onJobCheckboxToggled(event.target.checked, [
                        ...props.jobs,
                        ...props.outsideJobs,
                      ]);
                    }}
                    checked={fieldJobsChecked}
                    className={Styles.checkbox}
                  />
                </span>
                {LangFile.PrescriptionTableHead.field}: {getFieldName(props.field, LangFile)}
              </Typography>

              {!expanded && props.jobs.length === 0 && (
                <Typography variant={'subtitle2'} className={Styles.headerEmptyMessage}>
                  {LangFile.PrescriptionArchiveExpandableListItem.emptyMessage}
                </Typography>
              )}

              {expanded && (
                <>
                  <Typography variant={'subtitle2'} className={Styles.cropType}>
                    {LangFile.CropType[props.field.crop]}
                  </Typography>
                  <Typography variant={'body2'} className={Styles.sowingDate}>
                    {moment(props.field.sowing || selectedSeason.startDate).format('LL')}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          {!expanded && props.jobs.length > 0 && (
            <Table size={'small'} className={Styles.tableRoot}>
              <TableBody className={Styles.tableBodyTop}>
                <PrescriptionTableRow
                  alignText={'center'}
                  key={props.jobs[0].jobId}
                  job={props.jobs[0]}
                  totalPrescription={formatTotalPrescription(props.jobs[0])}
                  showSeasonSelector={false}
                  showDocumentation={false}
                  onSetSeason={props.onSetSeason}
                  onEdit={props.onEdit}
                  onDelete={props.onDelete}
                  onDownload={props.onDownload}
                  relatedJobName={firstJobRelatedJobName}
                  generateCSV={props.generateCSV([...props.jobs, ...props.outsideJobs])}
                />
              </TableBody>
            </Table>
          )}
          <IconButton size={'small'} className={Styles.collapseButton}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        {expanded && (
          <Box>
            <Table size={'small'}>
              <TableHead>
                <TableRow>
                  <TableCell className={Styles.headCell} align="left" padding="none">
                    {cols[0].label}
                  </TableCell>
                  <TableCell className={Styles.headCell} align="left" padding="none">
                    {cols[1].label}
                  </TableCell>
                  <TableCell className={Styles.headCell} align="left" padding="none">
                    {cols[2].label}
                  </TableCell>
                  <TableCell className={Styles.headCell} align="left" padding="none">
                    {cols[3].label}
                  </TableCell>
                  <TableCell className={Styles.headCell} align="left" padding="none">
                    {cols[4].label}
                  </TableCell>
                  <TableCell className={Styles.headCell} align="right" padding="none">
                    {' '}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody className={Styles.tableBody}>
                {props.jobs.map((job: PrescriptionJob) => {
                  const hasRelatedJob = job.vrmSet != null;
                  const relatedJobName = hasRelatedJob
                    ? props.jobs.find((j) => j.jobId === job.vrmSet.sourceVrmId)?.jobName
                    : undefined;
                  return (
                    <PrescriptionTableRow
                      key={job.jobId}
                      job={job}
                      totalPrescription={formatTotalPrescription(job)}
                      documentation={props.documentation}
                      showSeasonSelector={true}
                      showDocumentation={true}
                      onDocumentationUploaded={props.onDocumentationUploaded}
                      onDeleteDocumentation={props.onDeleteDocumentation}
                      onSetSeason={props.onSetSeason}
                      onEdit={props.onEdit}
                      onDelete={props.onDelete}
                      onDownload={props.onDownload}
                      relatedJobName={relatedJobName}
                      generateCSV={props.generateCSV([job])}
                      onJobCheckboxToggled={props.onJobCheckboxToggled}
                      selectedJobs={props.selectedJobs}
                    />
                  );
                })}
                {props.jobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className={Styles.tableRowEmpty}>
                      {LangFile.PrescriptionArchiveExpandableListItem.emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              {props.outsideJobs.length > 0 && (
                <TableBody className={Styles.tableBody}>
                  <TableRow>
                    <TableCell colSpan={5} className={Styles.outsideSeasonHeader}>
                      <Typography variant={'subtitle2'}>
                        {LangFile.PrescriptionArchiveExpandableListItem.outsideOfSeason}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {props.outsideJobs.map((job: PrescriptionJob) => {
                    const hasRelatedJob = job.vrmSet != null;
                    const relatedJobName = hasRelatedJob
                      ? props.outsideJobs.find((oJob) => oJob.jobId === job.vrmSet.sourceVrmId)
                          ?.jobName
                      : undefined;
                    return (
                      <PrescriptionTableRow
                        key={job.jobId}
                        job={job}
                        documentation={props.documentation}
                        showSeasonSelector={true}
                        showDocumentation={true}
                        onDocumentationUploaded={props.onDocumentationUploaded}
                        onDeleteDocumentation={props.onDeleteDocumentation}
                        onSetSeason={props.onSetSeason}
                        onEdit={props.onEdit}
                        onDelete={props.onDelete}
                        onDownload={props.onDownload}
                        relatedJobName={relatedJobName}
                        generateCSV={props.generateCSV([job])}
                        onJobCheckboxToggled={props.onJobCheckboxToggled}
                        selectedJobs={props.selectedJobs}
                      />
                    );
                  })}
                </TableBody>
              )}
            </Table>

            <Box className={Styles.bottom} />
          </Box>
        )}
      </Card>
    </ListItem>
  );
};

PrescriptionArchiveExpandableListItem.propTypes = {
  field: PropTypes.object.isRequired,
  jobs: PropTypes.arrayOf(PropTypes.instanceOf(PrescriptionJob)),
  outsideJobs: PropTypes.arrayOf(PropTypes.instanceOf(PrescriptionJob)),
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
  onSetSeason: PropTypes.func,
  onDocumentationUploaded: PropTypes.func,
  onDeleteDocumentation: PropTypes.func,
};

PrescriptionArchiveExpandableListItem.defaultProps = {
  jobs: [],
  outsideJobs: [],
  onDelete: voidFunc,
  onDownload: voidFunc,
  onEdit: voidFunc,
  onDocumentationUploaded: voidFunc,
  onDeleteDocumentation: voidFunc,
};

export default memo(PrescriptionArchiveExpandableListItem);
